// ---------------------------------------------------------------------------
// Mini Markdown renderer
// Supports: **bold**, *italic*, __underline__, ^superscript^, ~subscript~,
//           \n for line breaks, blank line for paragraph break.
// Usage: <Md>**ciao** mondo ~2~ ^3^</Md>
// ---------------------------------------------------------------------------
export function parseMd(text) {
  if (!text) return null;

  // Split in paragrafi (linee vuote)
  const paragraphs = text.split(/\n{2,}/);

  return paragraphs.map((para, pi) => {
    const lines = para.split('\n');
    const nodes = [];

    lines.forEach((line, li) => {
      const tokens = [];
      const re = /(\*\*[^*]+\*\*|\*[^*]+\*|__[^_]+__|\^[^^]+\^|~[^~]+~)/g;

      let last = 0;
      let m;

      while ((m = re.exec(line)) !== null) {
        // testo prima del match
        if (m.index > last) {
          tokens.push({
            type: 'text',
            value: line.slice(last, m.index),
          });
        }

        const val = m[0];

        if (val.startsWith('**')) {
          tokens.push({ type: 'bold', value: val.slice(2, -2) });
        } else if (val.startsWith('*')) {
          tokens.push({ type: 'italic', value: val.slice(1, -1) });
        } else if (val.startsWith('__')) {
          tokens.push({ type: 'underline', value: val.slice(2, -2) });
        } else if (val.startsWith('^')) {
          tokens.push({ type: 'sup', value: val.slice(1, -1) });
        } else if (val.startsWith('~')) {
          tokens.push({ type: 'sub', value: val.slice(1, -1) });
        }

        last = m.index + val.length;
      }

      // resto della riga
      if (last < line.length) {
        tokens.push({
          type: 'text',
          value: line.slice(last),
        });
      }

      // rendering React
      const rendered = tokens.map((t, i) => {
        switch (t.type) {
          case 'bold':
            return <strong key={i}>{t.value}</strong>;
          case 'italic':
            return <em key={i}>{t.value}</em>;
          case 'underline':
            return <u key={i}>{t.value}</u>;
          case 'sup':
            return <sup key={i}>{t.value}</sup>;
          case 'sub':
            return <sub key={i}>{t.value}</sub>;
          default:
            return <span key={i}>{t.value}</span>;
        }
      });

      nodes.push(<span key={`l${li}`}>{rendered}</span>);

      if (li < lines.length - 1) {
        nodes.push(<br key={`br${li}`} />);
      }
    });

    return (
        <p key={pi} className="md-paragraph">
          {nodes}
        </p>
    );
  });
}

export function Md({ children, className }) {
  if (!children) return null;
  return <div className={className}>{parseMd(children)}</div>;
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

export function LoadingSpinner() {
  return (
    <div className="d-flex align-items-center">
      <div className="progress-spinner progress-spinner-active" />
    </div>
  );
}

export function EmptyState({ message }) {
  return <div className="empty-state">{message}</div>;
}

// Plain DataTable used for secondary tables and default fallback
export function DataTable({ headers, rows, formatCell }) {
  return (
    <section className="obbligazioni-table">
      <div className="obbligazioni-table__wrapper">
        <table className="obbligazioni-table__table">
          <thead>
            <tr>
              {headers.map((header, idx) => (
                <th key={idx} scope="col">
                  <div className="obbligazioni-table__header">{header}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, colIndex) => (
                  <td key={colIndex}>
                    {formatCell ? formatCell(cell, rowIndex, colIndex, row) : String(cell ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function addYearsMonths (dateStr, years, months) {
  if (!dateStr) return null;

  const [day, month, year] = dateStr.split('/').map(Number);

  // mese totale (0-based)
  const totalMonths = (year * 12 + (month - 1)) + (years * 12 + months);

  const newYear = Math.floor(totalMonths / 12);
  const newMonth = totalMonths % 12;

  // ultimo giorno del mese target
  const lastDay = new Date(newYear, newMonth + 1, 0).getDate();

  const safeDay = Math.min(day, lastDay);

  return new Date(newYear, newMonth, safeDay);
}

export function formatMonthYearIT (date) {
  if (!(date instanceof Date)) return '-';

  const formatted = new Intl.DateTimeFormat('it-IT', {
    month: 'long',
    year: 'numeric',
  }).format(date);

  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}